import { loadScript } from "./load-script";

/**
 * Google Analytics 4, loaded without an inline `<script>`.
 *
 * The stock GA snippet is two inline scripts, and adding one would mean putting
 * `'unsafe-inline'` back on `script-src` — the one CSP directive this project
 * keeps strict (see CLAUDE.md). Everything the snippet does is done here instead:
 * the `dataLayer` queue and the `js`/`config` calls are plain module code, and
 * gtag.js arrives through `loadScript`, so both pages stay free of inline script.
 *
 * The queue is filled BEFORE the network request, exactly as the snippet does it.
 * gtag.js drains whatever is already in `window.dataLayer` when it evaluates, so
 * the first page_view is never lost to the load.
 */

/** The GA4 property. Public by nature — it ships in the page and in every hit. */
const MEASUREMENT_ID = "G-LPB2PP8S12";

const GTAG_SRC = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;

declare global {
  interface Window {
    // gtag queues `arguments` objects; GTM (lib/tag-manager.ts) pushes plain ones.
    dataLayer?: (IArguments | Record<string, unknown>)[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Hostnames that must never reach the property.
 *
 * `import.meta.env.DEV` is not enough: `pnpm serve` and `pnpm preview` run the
 * production build, so a dev-only check would still report every local page view
 * as real traffic. The host is what actually distinguishes them.
 *
 * `.lndo.site` is here because that is how the GoFundMe checkout is exercised
 * locally — it resolves to 127.0.0.1, but it is a real public hostname, so it
 * would otherwise look like production.
 */
export function isDevelopmentHost(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1" ||
    hostname === "[::1]" ||
    hostname.endsWith(".localhost") ||
    hostname.endsWith(".lndo.site")
  );
}

/**
 * Query flag that turns GA4 on from a development host, for DebugView.
 *
 * DebugView is the only way to see a hit ARRIVE — the console line `trackEvent`
 * prints locally proves the wiring fired, not that GA4 accepted it or how it
 * parsed the parameters. Reaching it needs two things at once, which is why one
 * flag does both: the host exclusion below has to be bypassed, and the hits have
 * to carry `debug_mode` or they land in the reports without ever showing up in
 * DebugView.
 *
 * Opt-in per page load, never sticky: `?ga_debug=1`. Nothing reads it in
 * production, and a visitor who somehow arrives with it on only flags their own
 * session as debug traffic.
 *
 * THESE ARE REAL HITS. They count in the reports like any other — GA4 has no
 * way to delete them afterwards. Use it deliberately, before a property starts
 * carrying campaign traffic, or accept the handful of rows.
 */
const DEBUG_FLAG = "ga_debug";

export function debugRequested(): boolean {
  try {
    return new URLSearchParams(window.location.search).has(DEBUG_FLAG);
  } catch {
    // A malformed query string is not worth failing analytics over.
    return false;
  }
}

let started = false;

/**
 * Starts GA4 for the current page. Safe to call more than once; only the first
 * call does anything, so the two entries can both ask without coordinating.
 *
 * No consent gate: the property is configured to collect on load. If a banner
 * (OneTrust or otherwise) ever has to gate this, the change is a
 * `gtag("consent", "default", …)` call above the `config` line — not a rewrite.
 */
export function initAnalytics(): void {
  if (typeof window === "undefined" || started) return;
  started = true;

  const debug = debugRequested();

  if (!debug && isDevelopmentHost(window.location.hostname)) {
    console.info(`[valvoline-analytics] skipped on ${window.location.hostname}`);
    return;
  }

  const dataLayer = (window.dataLayer ??= []);

  // MUST push `arguments`, and therefore MUST be a function declaration rather
  // than an arrow or a rest-parameter array: gtag.js checks that each queued
  // entry is an arguments object and ignores plain arrays, so `push([...])`
  // queues a command that is silently never executed.
  function gtag(): void {
    dataLayer.push(arguments);
  }

  const send = gtag as (...args: unknown[]) => void;
  window.gtag = send;

  if (debug) {
    console.info("[valvoline-analytics] debug_mode on — hits are real and will show in DebugView");
  }

  send("js", new Date());
  // page_view is sent by this call. Both pages are ordinary documents with real
  // URLs, so there is nothing to configure: no SPA routing, no manual page_view.
  // `debug_mode` set here carries to every later event on the page, so no event
  // has to know about it.
  send("config", MEASUREMENT_ID, debug ? { debug_mode: true } : {});

  void loadScript(GTAG_SRC).catch((error: unknown) => {
    // A blocked or failed gtag.js is not a page problem — the queue simply never
    // drains. Report it and carry on; an ad blocker is the usual cause.
    console.info("[valvoline-analytics] gtag.js did not load:", error);
  });
}

/**
 * Sends one GA4 event, and mirrors it onto the dataLayer for GTM.
 *
 * The single door out of this file: every custom event in the project goes
 * through here rather than calling `gtag()` in a section, so the property, the
 * dev-host exclusion and the parameter hygiene below stay in one place (see
 * CLAUDE.md — "give the module a function and call that").
 *
 * Parameters are pruned of `undefined`/`null`/`""` before sending. GA4 keeps
 * whatever it is given, and an empty string is a value: it shows up in reports
 * as a real "(not set)"-looking row that is impossible to tell from a genuine
 * one, so an unknown is better left off the hit entirely.
 *
 * On a development host `initAnalytics` never defines `window.gtag`, so nothing
 * is sent and the event is logged instead — which is how the wiring is checked
 * locally without polluting the property.
 */
export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  if (typeof window === "undefined") return;

  const clean: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    clean[key] = value;
  }

  const send = window.gtag;
  if (!send) {
    console.info("[valvoline-analytics] event (not sent):", name, clean);
    return;
  }

  send("event", name, clean);

  // The same event for GTM (lib/tag-manager.ts). A Custom Event trigger only
  // matches plain `{ event: … }` objects, never gtag's `arguments` entries, so
  // without this the container sees none of the events above. Harmless where
  // GTM is absent (/officialrules): gtag ignores objects in the queue.
  // Container tags must not forward these to G-LPB2PP8S12 — `send` already did.
  window.dataLayer?.push({ event: name, ...clean });
}
